/**
 * Rounds a number to a specified number of decimal places using the "round to even" strategy (banker's rounding).
 * This mimics the behavior of C#'s Math.Round method.
 * @param {number} value - The number to round.
 * @param {number} [precision=0] - The number of decimal places to round to.
 * @returns {number} The rounded value.
 */
const roundPrecision = (value, precision = 0) => {
  if (isNaN(value) || !isFinite(value)) return NaN

  // Handle negative precision (rounding to powers of 10)
  const p = 10 ** precision

  // Scale the value, round using "round to even", then scale back
  const scaled = value * p

  // For integers, just return the value as is
  if (Number.isInteger(scaled)) return value

  // Separate the integer and fractional parts
  const integer = Math.trunc(scaled)
  const fraction = scaled - integer

  // Get the first digit after the decimal point
  const firstFractionDigit = Math.abs(Math.trunc(fraction * 10))

  // Check if the value is exactly halfway between two integers
  const isHalfway = (fraction === 0.5 || fraction === -0.5) &&
    (firstFractionDigit === 5) &&
    (Math.abs(fraction * 10 - firstFractionDigit) < Number.EPSILON)

  let result
  if (isHalfway) {
    // For halfway cases, round to the nearest even number
    result = (integer % 2 === 0) ? integer : (scaled > 0 ? integer + 1 : integer - 1)
  } else {
    // For other cases, use standard rounding
    result = Math.round(scaled)
  }

  return result / p
}

export default roundPrecision

/**
 * Redondea un número exactamente igual que la función ROUND de Excel
 * @param {number} number - El número a redondear
 * @param {number} digits - Número de decimales (puede ser negativo)
 * @returns {number} - Número redondeado
 */
export function excelRound (number, digits = 0) {
  // Manejar casos especiales
  if (isNaN(number) || !isFinite(number)) {
    return number
  }

  if (isNaN(digits) || !isFinite(digits)) {
    throw new Error('El parámetro digits debe ser un número válido')
  }

  // Excel redondea digits a entero
  digits = Math.floor(digits)

  // Para evitar problemas de precisión de punto flotante
  const factor = 10 ** digits

  // Excel usa "redondeo comercial" o "redondeo a la mitad hacia arriba"
  // Esto significa que 0.5 siempre se redondea hacia arriba
  const sign = number >= 0 ? 1 : -1
  const absNumber = Math.abs(number)

  // Multiplicar por el factor y agregar 0.5 para redondeo comercial
  const shifted = absNumber * factor + 0.5

  // Truncar (quitar decimales) y dividir por el factor
  const rounded = Math.floor(shifted) / factor

  return rounded * sign
}

/**
 * Función alternativa usando el algoritmo más preciso
 * Maneja mejor los números muy grandes o muy pequeños
 */
export function excelRoundPrecise (number, digits = 0) {
  if (isNaN(number) || !isFinite(number)) {
    return number
  }

  digits = Math.floor(digits)

  // Convertir a string para evitar problemas de precisión
  const numberStr = number.toString()
  const [integerPart, decimalPart = ''] = numberStr.split('.')

  if (digits >= 0) {
    // Redondeo normal de decimales
    if (digits >= decimalPart.length) {
      return number // No hay nada que redondear
    }

    const factor = 10 ** digits

    return Math.round(number * factor) / factor
  }

  // Redondeo de enteros (digits negativo)
  const factor = 10 ** -digits

  return Math.round(number / factor) * factor
}

/**
 * Redondea un número al múltiplo superior más cercano de una base específica
 * Equivalente a ROUNDUP de Excel cuando se usa con potencias de 10
 * @param {number} number - El número a redondear
 * @param {number} digits - Número de decimales (negativo para redondear a decenas, centenas, etc.)
 * @returns {number} - Número redondeado hacia arriba
 */
export function roundUpToNearest (number, digits = 0) {
  if (isNaN(number) || !isFinite(number)) {
    return number
  }

  if (isNaN(digits) || !isFinite(digits)) {
    throw new Error('El parámetro digits debe ser un número válido')
  }

  digits = Math.floor(digits)

  // Para digits negativos (redondear a decenas, centenas, etc.)
  if (digits < 0) {
    const factor = 10 ** -digits

    return Math.ceil(number / factor) * factor
  }

  // Para digits positivos o cero
  const factor = 10 ** digits

  return Math.ceil(number * factor) / factor
}

/**
 * Función específica para redondear al 100 superior más cercano
 * @param {number} number - El número a redondear
 * @returns {number} - Número redondeado al 100 superior
 */
export function roundUpToHundred (number) {
  return Math.ceil(number / 100) * 100
}
